/**
 * free-gift.js
 *
 * Responsibility: Automatically manage the complimentary Free Gift in the cart.
 * - When any paid product is added/present in the cart, exactly 1 Free Gift is added.
 * - If all paid products are removed from the cart, the Free Gift is completely removed and cart is emptied.
 * - Free gift quantity is strictly locked to 1 (cannot be incremented or duplicated).
 * - Guaranteed free gift injection before checkout.
 *
 * AGENTS.md compliant:
 * - Pure business logic functions separated from DOM operations.
 * - Enclosed in IIFE; no globals.
 * - Centralized DOM queries with data-* attributes.
 * - Defensive try/catch around all DOM and network operations.
 */

(function initFreeGiftModule() {
  'use strict';

  const FREE_GIFT_VARIANT_ID = 56088915280194;
  const FREE_GIFT_PRODUCT_ID = 10513184358722;
  const FREE_GIFT_HANDLE = 'blue-lock-mystery-collectible-and-redeem-card';

  let isClearing = false;
  let syncPromise = null;
  let drawerObserver = null;

  // ─── Pure Business Logic ──────────────────────────────────────────────────

  /**
   * Checks if an item represents the free gift.
   * Pure function.
   * @param {object} item
   * @returns {boolean}
   */
  function isFreeGiftItem(item) {
    if (!item) return false;
    const variantId = Number(item.id || item.variant_id);
    const productId = Number(item.product_id);
    const handle = String(item.handle || '');
    const isTagGift = Array.isArray(item.tags) && item.tags.includes('free gift');
    const isPropGift = Boolean(item.properties && (item.properties._is_free_gift === 'true' || item.properties._free_gift === 'true'));

    return (
      variantId === FREE_GIFT_VARIANT_ID ||
      productId === FREE_GIFT_PRODUCT_ID ||
      handle === FREE_GIFT_HANDLE ||
      isTagGift ||
      isPropGift
    );
  }

  /**
   * Checks if the cart contains at least one paid (non-gift) product.
   * Pure function.
   * @param {object} cart
   * @returns {boolean}
   */
  function hasPaidItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (item) {
      return !isFreeGiftItem(item);
    });
  }

  /**
   * Finds the free gift item in the cart, if present.
   * Pure function.
   * @param {object} cart
   * @returns {object|null}
   */
  function getFreeGiftFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find(isFreeGiftItem) || null;
  }

  // ─── DOM Helpers ──────────────────────────────────────────────────────────

  /**
   * Queries existing cart section IDs from the DOM for Section Rendering API updates.
   * @returns {string[]}
   */
  function getCartSectionIds() {
    const sectionIds = [];
    try {
      const elements = document.querySelectorAll('[data-section-id]');
      elements.forEach(function (el) {
        if (el.closest('cart-drawer-component, theme-drawer#cart-drawer, .cart-page, cart-items-component')) {
          const id = el.getAttribute('data-section-id');
          if (id && !sectionIds.includes(id)) {
            sectionIds.push(id);
          }
        }
      });
      if (sectionIds.length === 0 && document.getElementById('shopify-section-cart-drawer-section')) {
        sectionIds.push('cart-drawer-section');
      }
    } catch (_) {}
    return sectionIds;
  }

  /**
   * Gets all rendered cart item rows in the DOM.
   * @returns {HTMLElement[]}
   */
  function getCartRows() {
    return Array.from(document.querySelectorAll('.cart-items__table-row, [ref="cartItemRows[]"]'));
  }

  /**
   * Gets all rendered paid item rows in the DOM.
   * @returns {HTMLElement[]}
   */
  function getPaidCartRows() {
    return getCartRows().filter(function (row) {
      return row.getAttribute('data-is-free-gift') !== 'true';
    });
  }

  /**
   * Gets all rendered free gift rows in the DOM.
   * @returns {HTMLElement[]}
   */
  function getGiftCartRows() {
    return getCartRows().filter(function (row) {
      return row.getAttribute('data-is-free-gift') === 'true';
    });
  }

  /**
   * Updates cart bubble count and badges across the header and drawer to 0.
   */
  function updateCartCountsToZero() {
    try {
      const bubbleCounts = document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count');
      bubbleCounts.forEach(function (el) {
        el.textContent = '0';
        el.classList.add('hidden');
      });

      const bubbles = document.querySelectorAll('[ref="cartBubble"], .cart-bubble');
      bubbles.forEach(function (el) {
        el.classList.add('visually-hidden');
      });

      const badges = document.querySelectorAll('.theme-drawer__badge');
      badges.forEach(function (badge) {
        badge.textContent = '0';
        badge.classList.add('visually-hidden');
      });
    } catch (_) {}
  }

  /**
   * Optimistically renders the empty cart state in the DOM to avoid UI flashes.
   */
  function renderEmptyCartStateOptimistically() {
    try {
      const template = document.getElementById('empty-cart-template');
      const cartItems = document.querySelector('cart-items-component');
      if (template instanceof HTMLTemplateElement && cartItems) {
        const clone = document.importNode(template.content, true);
        const heading = document.getElementById('cart-drawer-heading');
        if (heading) heading.remove();
        cartItems.replaceChildren(clone);
      }

      const summaries = document.querySelectorAll('.cart-drawer__summary, .cart-page__summary');
      summaries.forEach(function (summary) {
        summary.style.display = 'none';
      });

      const drawerInner = document.querySelector('.cart-drawer__inner');
      if (drawerInner) {
        drawerInner.classList.add('cart-drawer--empty');
      }

      updateCartCountsToZero();
    } catch (_) {}
  }

  /**
   * Dispatches theme events and standard Shopify events to refresh UI components.
   * @param {object} [sections]
   * @param {number} [itemCount]
   * @param {Array} [items]
   */
  function triggerCartUIRefresh(sections, itemCount, items) {
    const count = typeof itemCount === 'number' ? itemCount : 0;
    const lineItems = Array.isArray(items) ? items : [];
    const sectionsObj = sections && typeof sections === 'object' ? sections : {};

    try {
      const updateEvent = new CustomEvent('shopify:cart:lines-update', {
        bubbles: true,
        cancelable: true
      });
      updateEvent.promise = Promise.resolve({
        cart: {
          totalQuantity: count,
          lines: lineItems
        },
        detail: {
          sections: sectionsObj,
          itemCount: count,
          items: lineItems
        }
      });
      document.dispatchEvent(updateEvent);
    } catch (_) {}

    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('shopify:cart:view', { bubbles: true }));
    } catch (_) {}
  }

  // ─── Network Actions ──────────────────────────────────────────────────────

  /**
   * Completely clears the cart and resets UI when no paid items remain.
   * @returns {Promise<void>}
   */
  async function emptyCartCompletely() {
    if (isClearing) return;
    isClearing = true;

    try {
      // 1. Optimistically display empty state immediately
      renderEmptyCartStateOptimistically();

      // 2. Clear cart on Shopify server
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: '{}'
      });

      // 3. Request fresh empty section HTML from Shopify Section Rendering API
      const sectionIds = getCartSectionIds();
      let sectionsData = {};
      if (sectionIds.length > 0) {
        try {
          const sectionsQuery = encodeURIComponent(sectionIds.join(','));
          const res = await fetch(`${window.location.pathname}?sections=${sectionsQuery}&t=${Date.now()}`, {
            headers: { 'Accept': 'application/json' },
            cache: 'no-store'
          });
          if (res.ok) {
            sectionsData = await res.json();
          }
        } catch (_) {}
      }

      // 4. Dispatch standard Shopify cart events
      triggerCartUIRefresh(sectionsData, 0, []);
      updateCartCountsToZero();
    } catch (error) {
      console.warn('[free-gift] Failed to empty cart completely:', error);
    } finally {
      isClearing = false;
    }
  }

  /**
   * Resets free gift quantity strictly to 1.
   * @returns {Promise<boolean>}
   */
  async function resetFreeGiftQuantity() {
    try {
      const sectionIds = getCartSectionIds();
      const payload = {
        id: String(FREE_GIFT_VARIANT_ID),
        quantity: 1
      };
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(',');
      }
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        triggerCartUIRefresh(data.sections, data.item_count, data.items);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * Adds exactly 1 free gift variant to the cart.
   * Guarded against duplicate additions.
   * @returns {Promise<boolean>}
   */
  async function addFreeGift() {
    try {
      // 1. Verify latest cart to prevent duplicate adds
      const checkRes = await fetch('/cart.js?t=' + Date.now(), {
        headers: { 'Accept': 'application/json' },
        cache: 'no-store'
      });
      if (checkRes.ok) {
        const currentCart = await checkRes.json();
        const existingGift = getFreeGiftFromCart(currentCart);
        if (existingGift) {
          if (existingGift.quantity !== 1) {
            return resetFreeGiftQuantity();
          }
          return true;
        }
      }

      // 2. Add free gift
      const sectionIds = getCartSectionIds();
      const payload = {
        items: [
          {
            id: FREE_GIFT_VARIANT_ID,
            quantity: 1,
            properties: {
              '_is_free_gift': 'true',
              'Gift': 'Free Collectible Card'
            }
          }
        ]
      };
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(',');
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        // Check if quantity accidentally exceeded 1
        const giftInResponse = Array.isArray(data.items) ? data.items.find(isFreeGiftItem) : null;
        if (giftInResponse && giftInResponse.quantity > 1) {
          return resetFreeGiftQuantity();
        }

        const itemCount = Array.isArray(data.items)
          ? data.items.reduce(function (sum, item) { return sum + (item.quantity || 0); }, 0)
          : 1;
        triggerCartUIRefresh(data.sections, itemCount, data.items);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * Removes the free gift variant from the cart.
   * @returns {Promise<boolean>}
   */
  async function removeFreeGift() {
    try {
      const sectionIds = getCartSectionIds();
      const payload = {
        id: String(FREE_GIFT_VARIANT_ID),
        quantity: 0
      };
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(',');
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        triggerCartUIRefresh(data.sections, data.item_count, data.items);
        if (data.item_count === 0) {
          updateCartCountsToZero();
        }
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  // ─── Reconciliation & Guards ──────────────────────────────────────────────

  /**
   * Reconciles cart state to ensure the free gift matches business rules.
   * Uses syncPromise mutex lock to prevent concurrent executions.
   * @param {object} [providedCart]
   * @returns {Promise<void>}
   */
  async function reconcileCart(providedCart) {
    if (isClearing) return;
    if (syncPromise) return syncPromise;

    syncPromise = (async function executeReconcile() {
      try {
        let cart = providedCart;
        if (!cart || !Array.isArray(cart.items)) {
          const res = await fetch('/cart.js?t=' + Date.now(), {
            headers: { 'Accept': 'application/json' },
            cache: 'no-store'
          });
          if (!res.ok) return;
          cart = await res.json();
        }

        const hasPaid = hasPaidItems(cart);
        const giftItem = getFreeGiftFromCart(cart);

        if (hasPaid && !giftItem) {
          // Add exactly 1 free gift
          await addFreeGift();
        } else if (!hasPaid && giftItem) {
          // Only gift in cart, wipe whole cart
          await emptyCartCompletely();
        } else if (giftItem && giftItem.quantity !== 1) {
          // Enforce strictly 1 gift per order
          await resetFreeGiftQuantity();
        }
      } catch (err) {
        console.warn('[free-gift] Reconcile error:', err);
      } finally {
        syncPromise = null;
      }
    })();

    return syncPromise;
  }

  /**
   * Checks if the DOM currently contains ONLY the free gift row with zero paid items.
   * If so, immediately triggers emptyCartCompletely().
   */
  function checkAndRemoveLoneGiftInDOM() {
    if (isClearing || syncPromise) return;

    try {
      const allRows = getCartRows();
      if (allRows.length === 0) return;

      const paidRows = getPaidCartRows();
      const giftRows = getGiftCartRows();

      if (paidRows.length === 0 && giftRows.length > 0) {
        emptyCartCompletely();
      }
    } catch (_) {}
  }

  // ─── Event Interception ───────────────────────────────────────────────────

  /**
   * Intercepts capture clicks on remove buttons, minus buttons, and checkout.
   * @param {MouseEvent} event
   */
  function handleCaptureClick(event) {
    const target = event.target;
    if (!target || !(target instanceof Element)) return;

    // 1. Intercept remove button on the last paid item
    const removeBtn = target.closest('.cart-items__remove');
    if (removeBtn) {
      const row = removeBtn.closest('.cart-items__table-row, [ref="cartItemRows[]"]');
      if (row && row.getAttribute('data-is-free-gift') !== 'true') {
        const paidRows = getPaidCartRows();
        if (paidRows.length <= 1) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          emptyCartCompletely();
          return;
        }
      }
    }

    // 2. Intercept minus button on the last paid item when quantity is 1
    const minusBtn = target.closest('button[name="minus"]');
    if (minusBtn) {
      const row = minusBtn.closest('.cart-items__table-row, [ref="cartItemRows[]"]');
      if (row && row.getAttribute('data-is-free-gift') !== 'true') {
        const input = row.querySelector('input[name="updates[]"], input[type="number"]');
        if (input && Number(input.value) <= 1) {
          const paidRows = getPaidCartRows();
          if (paidRows.length <= 1) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            emptyCartCompletely();
            return;
          }
        }
      }
    }

    // 3. Intercept checkout to guarantee free gift is present before navigating
    const checkoutBtn = target.closest('#checkout, button[name="checkout"], .cart__checkout-button');
    if (checkoutBtn) {
      const rows = getCartRows();
      if (rows.length === 0) return;

      const hasPaid = getPaidCartRows().length > 0;
      const hasGift = getGiftCartRows().length > 0;

      if (hasPaid && !hasGift) {
        event.preventDefault();
        addFreeGift()
          .then(function () {
            const form = checkoutBtn.closest('form') || document.getElementById('cart-form');
            if (form) {
              form.submit();
            } else {
              window.location.href = '/checkout';
            }
          })
          .catch(function () {
            window.location.href = '/checkout';
          });
      }
    }
  }

  /**
   * Intercepts manual quantity input changes to 0 on the last paid item.
   * @param {Event} event
   */
  function handleQuantityChange(event) {
    const target = event.target;
    if (!target || !(target instanceof HTMLInputElement)) return;

    if (target.matches('input[name="updates[]"], cart-quantity-selector-component input')) {
      if (Number(target.value) <= 0) {
        const row = target.closest('.cart-items__table-row, [ref="cartItemRows[]"]');
        if (row && row.getAttribute('data-is-free-gift') !== 'true') {
          const paidRows = getPaidCartRows();
          if (paidRows.length <= 1) {
            event.preventDefault();
            event.stopPropagation();
            emptyCartCompletely();
          }
        }
      }
    }
  }

  /**
   * Sets up MutationObserver on the cart drawer to catch any DOM state where only the free gift remains.
   */
  function setupMutationObserver() {
    try {
      const drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer-component');
      if (!drawer) return;

      if (drawerObserver) {
        drawerObserver.disconnect();
      }

      drawerObserver = new MutationObserver(function () {
        checkAndRemoveLoneGiftInDOM();
      });

      drawerObserver.observe(drawer, {
        childList: true,
        subtree: true
      });
    } catch (_) {}
  }

  /**
   * Attaches lifecycle event listeners.
   */
  function attachEventListeners() {
    // Capture-phase click intercept for remove, minus, and checkout
    document.addEventListener('click', handleCaptureClick, true);

    // Quantity input changes
    document.addEventListener('change', handleQuantityChange, true);

    // Reconcile on cart lines changes (with promise support)
    document.addEventListener('shopify:cart:lines-update', function (e) {
      if (e && e.promise) {
        e.promise
          .then(function (result) {
            const cart = result && result.cart ? result.cart : null;
            reconcileCart(cart);
          })
          .catch(function () {
            reconcileCart();
          });
      } else {
        reconcileCart();
      }
    });

    document.addEventListener('cart:updated', function () {
      reconcileCart();
    });

    document.addEventListener('cart:refresh', function () {
      reconcileCart();
    });

    document.addEventListener('theme-drawer:open', function () {
      checkAndRemoveLoneGiftInDOM();
      reconcileCart();
    });

    document.addEventListener('drawer:open', function () {
      checkAndRemoveLoneGiftInDOM();
      reconcileCart();
    });
  }

  function init() {
    attachEventListeners();
    setupMutationObserver();
    checkAndRemoveLoneGiftInDOM();
    reconcileCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
